import type { AnomalyScenario, MetaphorSelection } from './model.ts'

export const SCENARIOS = [
  {
    id: 'unsewn-thread',
    number: 1,
    kind: 'thread',
    name: '꿰매지지 않은 실',
    code: 'MF-01 / THREAD',
    brief:
      '자르려 할수록 길어지는 검은 실이 방 안의 사물을 한 줄로 묶고 있다.',
    question: '힘을 쓰지 않고 이 실을 멈출 비유를 만드세요.',
    sentenceLead: '검은 실은 ',
    requiredTraits: ['bounded', 'gentle', 'named'],
    forbiddenTraits: ['forceful', 'endless', 'hungry', 'forgetful'],
    inheritance: 'none',
    stableCopy: '실이 이름 붙은 매듭 하나로 접힌다.',
    unstableCopy: '실이 문장의 빈틈으로 다시 번진다.',
    slots: [
      {
        id: 'thread-form',
        label: '형태',
        prompt: '이 실은 무엇과 닮았나요?',
        chips: [
          {
            id: 'thread-form-ribbon',
            text: '접어 둔 리본처럼',
            resonance: '끝이 보이고, 만질 수 있다.',
          },
          {
            id: 'thread-form-storm',
            text: '끝없는 폭풍의 꼬리처럼',
            resonance: '끝이 없고 힘으로 밀어붙인다.',
          },
          {
            id: 'thread-form-mouth',
            text: '배고픈 입술처럼',
            resonance: '닿는 것을 계속 원한다.',
          },
        ],
      },
      {
        id: 'thread-motion',
        label: '움직임',
        prompt: '어떤 방식으로 움직이나요?',
        chips: [
          {
            id: 'thread-motion-drag',
            text: '방 안의 모든 것을 억지로 끌어당기고',
            resonance: '저항을 허용하지 않는다.',
          },
          {
            id: 'thread-motion-breath',
            text: '숨결을 따라 천천히 둥글어지고',
            resonance: '밀지 않아도 되돌아온다.',
          },
          {
            id: 'thread-motion-devour',
            text: '닿는 경계를 끝없이 삼키고',
            resonance: '멈출 이유를 잃는다.',
          },
        ],
      },
      {
        id: 'thread-anchor',
        label: '닻',
        prompt: '무엇이 이 비유를 닫나요?',
        chips: [
          {
            id: 'thread-anchor-nameless',
            text: '이름 없이 벽 너머로 계속 번진다.',
            resonance: '붙잡을 말도 마지막도 없다.',
          },
          {
            id: 'thread-anchor-blade',
            text: '가장 큰 칼로 베어야만 풀려난다.',
            resonance: '해방은 있지만 강제가 남는다.',
          },
          {
            id: 'thread-anchor-name',
            text: '‘첫 매듭’이라 부르면 손바닥 안에서 멎는다.',
            resonance: '이름과 자리가 동시에 생긴다.',
          },
        ],
      },
    ],
  },
  {
    id: 'aftertone-bell',
    number: 2,
    kind: 'bell',
    name: '먼저 울린 종',
    code: 'MF-02 / BELL',
    brief:
      '소리보다 먼저 울리는 종이 듣지 못한 사람의 이름을 가져간다.',
    question: '종과 듣는 사람이 한 번씩 답하는 비유를 만드세요.',
    sentenceLead: '먼저 울린 종은 ',
    requiredTraits: ['remembering', 'reciprocal', 'timed'],
    forbiddenTraits: ['forceful', 'endless', 'hungry', 'forgetful'],
    inheritance: 'none',
    stableCopy: '종이 빌린 이름을 제 박자에 돌려준다.',
    unstableCopy: '대답 없는 울림이 이름을 덮는다.',
    slots: [
      {
        id: 'bell-form',
        label: '형태',
        prompt: '이 종은 무엇과 닮았나요?',
        chips: [
          {
            id: 'bell-form-mouth',
            text: '굶주린 금속의 입처럼',
            resonance: '대답보다 이름을 원한다.',
          },
          {
            id: 'bell-form-promise',
            text: '오래된 약속처럼',
            resonance: '기억한 때에 돌아온다.',
          },
          {
            id: 'bell-form-footprint',
            text: '비에 지워진 발자국처럼',
            resonance: '누가 지나갔는지 남지 않는다.',
          },
        ],
      },
      {
        id: 'bell-motion',
        label: '응답',
        prompt: '울림은 무엇을 주고받나요?',
        chips: [
          {
            id: 'bell-motion-hold',
            text: '듣는 이를 영원히 붙잡아 두고',
            resonance: '응답이 구속으로 변한다.',
          },
          {
            id: 'bell-motion-repeat',
            text: '주인 없는 메아리만 되풀이하고',
            resonance: '반복되지만 상대를 기억하지 못한다.',
          },
          {
            id: 'bell-motion-return',
            text: '빌린 이름을 한 번 돌려주고',
            resonance: '받은 만큼 정확히 되돌린다.',
          },
        ],
      },
      {
        id: 'bell-anchor',
        label: '박자',
        prompt: '언제 울림이 끝나나요?',
        chips: [
          {
            id: 'bell-anchor-two-knocks',
            text: '두 번의 노크가 답하면 제시간에 잠든다.',
            resonance: '질문과 답이 한 쌍으로 닫힌다.',
          },
          {
            id: 'bell-anchor-forever',
            text: '누구도 오지 않아도 끝없이 기다린다.',
            resonance: '마지막 박자가 없다.',
          },
          {
            id: 'bell-anchor-feed',
            text: '다음 이름을 삼켜야 조용해진다.',
            resonance: '끝의 대가가 또 다른 희생이다.',
          },
        ],
      },
    ],
  },
  {
    id: 'borrowed-mirror',
    number: 3,
    kind: 'mirror',
    name: '문장을 훔치는 거울',
    code: 'MF-03 / MIRROR',
    brief:
      '거울이 앞선 두 문장의 성질을 자기 표면에 새겼다.',
    question: '앞서 남긴 뜻을 이어 붙여 반사와 해방을 완성하세요.',
    sentenceLead: '문장을 훔친 거울은 ',
    requiredTraits: ['bounded', 'remembering', 'reflective', 'released'],
    forbiddenTraits: ['forceful', 'endless', 'hungry', 'forgetful'],
    inheritance: 'all-previous-success-traits',
    stableCopy: '거울이 빌린 문장을 비추고 모두 놓아준다.',
    unstableCopy: '거울이 문장의 흉내를 붙든다.',
    slots: [
      {
        id: 'mirror-form',
        label: '표면',
        prompt: '거울은 무엇과 닮았나요?',
        chips: [
          {
            id: 'mirror-form-window',
            text: '바닥 없는 창처럼',
            resonance: '표면 뒤에 마지막 지점이 없다.',
          },
          {
            id: 'mirror-form-copy',
            text: '굶주린 복제품처럼',
            resonance: '원본을 가질수록 더 원한다.',
          },
          {
            id: 'mirror-form-sentence',
            text: '앞선 두 문장의 뒷면처럼',
            resonance: '받은 의미를 반대편에서 보여 준다.',
          },
        ],
      },
      {
        id: 'mirror-motion',
        label: '반사',
        prompt: '받은 의미를 어떻게 다루나요?',
        chips: [
          {
            id: 'mirror-motion-reflect',
            text: '받은 의미를 뒤집어 비추고',
            resonance: '훔치지 않고 방향만 바꾼다.',
          },
          {
            id: 'mirror-motion-erase',
            text: '원본을 지워 그 자리에 서고',
            resonance: '반사가 대체가 된다.',
          },
          {
            id: 'mirror-motion-capture',
            text: '모든 얼굴을 끝없이 붙들고',
            resonance: '비춘 것을 보내지 않는다.',
          },
        ],
      },
      {
        id: 'mirror-anchor',
        label: '반환',
        prompt: '어떻게 문장을 돌려주나요?',
        chips: [
          {
            id: 'mirror-anchor-break',
            text: '표면을 깨뜨려야만 출구를 연다.',
            resonance: '해방에 파괴가 섞인다.',
          },
          {
            id: 'mirror-anchor-release',
            text: '마지막 이름까지 비추면 모두 놓아준다.',
            resonance: '반사의 끝에서 소유를 포기한다.',
          },
          {
            id: 'mirror-anchor-memory',
            text: '기억을 삼킬수록 더 또렷해진다.',
            resonance: '기억을 되돌려주지 않는다.',
          },
        ],
      },
    ],
  },
] as const satisfies readonly [AnomalyScenario, AnomalyScenario, AnomalyScenario]

export const GUARANTEED_WIN_SELECTIONS = [
  {
    'thread-form': 'thread-form-ribbon',
    'thread-motion': 'thread-motion-breath',
    'thread-anchor': 'thread-anchor-name',
  },
  {
    'bell-form': 'bell-form-promise',
    'bell-motion': 'bell-motion-return',
    'bell-anchor': 'bell-anchor-two-knocks',
  },
  {
    'mirror-form': 'mirror-form-sentence',
    'mirror-motion': 'mirror-motion-reflect',
    'mirror-anchor': 'mirror-anchor-release',
  },
] as const satisfies readonly [
  MetaphorSelection,
  MetaphorSelection,
  MetaphorSelection,
]

export const INITIAL_SELECTION: MetaphorSelection = {
  'thread-form': GUARANTEED_WIN_SELECTIONS[0]['thread-form'],
  'thread-motion': GUARANTEED_WIN_SELECTIONS[0]['thread-motion'],
}

export const GUARANTEED_LOSS_SELECTIONS = [
  {
    'thread-form': 'thread-form-storm',
    'thread-motion': 'thread-motion-drag',
    'thread-anchor': 'thread-anchor-nameless',
  },
  {
    'thread-form': 'thread-form-storm',
    'thread-motion': 'thread-motion-drag',
    'thread-anchor': 'thread-anchor-nameless',
  },
] as const satisfies readonly [MetaphorSelection, MetaphorSelection]
